import {Color} from '@benchling/nucleus-ui';
import {EdgeTypes, Node as ReactFlowNode} from 'reactflow';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';
import {SimpleBenchlingNodeData} from 'ui-library/reactflow/nodes/SimpleBenchlingNode/SimpleBenchlingNodeTypes';

import {RelationshipsForSchema, SchemaDomainTypeInterface} from './DomainGraphTypesForSchemaDiagram';

interface SchemaDomainData {
  id: string;
  domainTypeInterface: string;
  relationships: RelationshipsForSchema;
}

export type SchemaNodeData = SchemaDomainData & SimpleBenchlingNodeData;

export type SchemaDiagramNode = ReactFlowNode<SchemaNodeData, 'schema'>;

export const SchemaDiagramEdgeTypes = {} satisfies EdgeTypes;

export const NODE_HEIGHT = 20;
export const NODE_WIDTH = 100;

export const DIAGRAM_HEIGHT = 800;
export const DIAGRAM_WIDTH = 1500;

export const INTERFACE_ICONS: Record<SchemaDomainTypeInterface, BenchlingIconName> = {
  [SchemaDomainTypeInterface.IDnaSequence]: FolderItemIcon.Sequence,
  [SchemaDomainTypeInterface.IDnaOligo]: FolderItemIcon.Oligo,
  [SchemaDomainTypeInterface.IRnaSequence]: FolderItemIcon.RNASequence,
  [SchemaDomainTypeInterface.IRnaOligo]: FolderItemIcon.OligoV2,
  [SchemaDomainTypeInterface.IAaSequence]: FolderItemIcon.Protein,
  [SchemaDomainTypeInterface.IProtein]: FolderItemIcon.Antibody,
  [SchemaDomainTypeInterface.IMolecule]: FolderItemIcon.Molecule,
  [SchemaDomainTypeInterface.IPlate]: ModelTypeConstants.PLATE,
  [SchemaDomainTypeInterface.IMatrixPlate]: ModelTypeConstants.PLATE,
  [SchemaDomainTypeInterface.IWellPlate]: ModelTypeConstants.PLATE,
  [SchemaDomainTypeInterface.IContainer]: ModelTypeConstants.CONTAINER,
  [SchemaDomainTypeInterface.IBox]: ModelTypeConstants.BOX,
  [SchemaDomainTypeInterface.ILocation]: ModelTypeConstants.LOCATION,
  [SchemaDomainTypeInterface.IMixture]: FolderItemIcon.Mixture,
  [SchemaDomainTypeInterface.ICustomEntity]: FolderItemIcon.BasicFolderItem,
  [SchemaDomainTypeInterface.IEntry]: FolderItemIcon.Entry,
  [SchemaDomainTypeInterface.IResult]: ModelTypeConstants.RESULT_SCHEMA,
  [SchemaDomainTypeInterface.IRun]: ModelTypeConstants.RUN_SCHEMA,
};

export const INVENTORY_COLOR = '#E35203';

export const SCHEMA_COLORS: Record<SchemaDomainTypeInterface, string> = {
  [SchemaDomainTypeInterface.IAaSequence]: '#A000B5',
  [SchemaDomainTypeInterface.IBox]: INVENTORY_COLOR,
  [SchemaDomainTypeInterface.IContainer]: INVENTORY_COLOR,
  [SchemaDomainTypeInterface.ICustomEntity]: Color.TEAL_500,
  [SchemaDomainTypeInterface.IDnaOligo]: Color.RED_500,
  [SchemaDomainTypeInterface.IDnaSequence]: Color.GRAPE_500,
  [SchemaDomainTypeInterface.IEntry]: '#00B5A6',
  [SchemaDomainTypeInterface.ILocation]: INVENTORY_COLOR,
  [SchemaDomainTypeInterface.IMatrixPlate]: INVENTORY_COLOR,
  [SchemaDomainTypeInterface.IMixture]: Color.BLUE_500,
  [SchemaDomainTypeInterface.IMolecule]: Color.PINK_500,
  [SchemaDomainTypeInterface.IPlate]: INVENTORY_COLOR,
  [SchemaDomainTypeInterface.IProtein]: '#A000B5',
  [SchemaDomainTypeInterface.IResult]: Color.LIME_500,
  [SchemaDomainTypeInterface.IRnaSequence]: Color.GREEN_500,
  [SchemaDomainTypeInterface.IRnaOligo]: Color.ORANGE_500,
  [SchemaDomainTypeInterface.IRun]: Color.YELLOW_500,
  [SchemaDomainTypeInterface.IWellPlate]: INVENTORY_COLOR,
};

export const INVENTORY_BACKGROUND_COLOR = '#FCEEE6';

export const SCHEMA_BACKGROUND_COLORS: Record<SchemaDomainTypeInterface, string> = {
  [SchemaDomainTypeInterface.IAaSequence]: '#F8EbF9',
  [SchemaDomainTypeInterface.IBox]: INVENTORY_BACKGROUND_COLOR,
  [SchemaDomainTypeInterface.IContainer]: INVENTORY_BACKGROUND_COLOR,
  [SchemaDomainTypeInterface.ICustomEntity]: Color.TEAL_100,
  [SchemaDomainTypeInterface.IDnaOligo]: Color.RED_100,
  [SchemaDomainTypeInterface.IDnaSequence]: Color.GRAPE_100,
  [SchemaDomainTypeInterface.IEntry]: '#E5F7F6',
  [SchemaDomainTypeInterface.ILocation]: INVENTORY_BACKGROUND_COLOR,
  [SchemaDomainTypeInterface.IMatrixPlate]: INVENTORY_BACKGROUND_COLOR,
  [SchemaDomainTypeInterface.IMixture]: Color.BLUE_100,
  [SchemaDomainTypeInterface.IMolecule]: Color.PINK_100,
  [SchemaDomainTypeInterface.IPlate]: INVENTORY_BACKGROUND_COLOR,
  [SchemaDomainTypeInterface.IProtein]: '#F8EBF9',
  [SchemaDomainTypeInterface.IResult]: Color.LIME_100,
  [SchemaDomainTypeInterface.IRnaSequence]: Color.GREEN_100,
  [SchemaDomainTypeInterface.IRnaOligo]: Color.ORANGE_100,
  [SchemaDomainTypeInterface.IRun]: Color.YELLOW_100,
  [SchemaDomainTypeInterface.IWellPlate]: INVENTORY_BACKGROUND_COLOR,
};

export const SCHEMA_DIAGRAM_COORDINATES_PREF_KEY = 'DATA_MODEL_COORDINATES';
